import React, { useState, useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import axios from 'axios'
import { API_BASE_URL, getAgentById, getEndUserSubscriptions_Billings, getPaymentMethods, getBillingInvoices, getUpcomingInvoice, payInvoice, getReactivationStatus, reactivateSubscription } from '../../Store/apiStore'
import { stripePromise } from '../../lib/stripeClient'
import { useCheckoutStore } from '../../Store/checkoutStore'
import isEmbeddedCheckoutEnabled from '../../lib/embeddedCheckoutFlag'
import styles from './UsageHistory.module.css'
import HeaderBar from '../HeaderBar/HeaderBar'
import OfferBanner from './OfferBanner'
import PlanCard from './PlanCard'
import UsageCard from './UsageCard'
import UsageSummary from './UsageSummary'
import BillingMilestones from './BillingMilestones'
import CurrentCharges from './CurrentCharges'
import BillingWorksBanner from './BillingWorksBanner'
import BillingHistory from './BillingHistory'
import BillingInfo from './BillingInfo'
import PopUp from '../Popup/Popup'

const UsageHistory = () => {
  const [activeTab, setActiveTab] = useState('usage')

  // Agent id arrives from the More-menu (FooterNew) via router state, with a
  // sessionStorage fallback so a page refresh keeps it.
  const location = useLocation()
  const navigate = useNavigate()
  const agentId = location?.state?.agentId || sessionStorage.getItem('SelectAgentId')

  // "Nov 1" style short date (billing period). Empty for missing/invalid dates.
  const fmtDay = (d) => {
    if (!d) return ''
    const dt = new Date(d)
    return isNaN(dt) ? '' : dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
  }

  // "Oct 15, 2026" style — WITH year — for the Cancel "active until" line.
  const fmtFull = (d) => {
    if (!d) return ''
    const dt = new Date(d)
    return isNaN(dt) ? '' : dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
  }

  // Current Plan card data:
  //   plan.name  ← agentdetails.agentPlan (AGENT table)                via getAgentById
  //   plan.fee   ← monthly_fee ($5, flat Stripe line item)            via /agent-payg-details
  // Current Usage card data:
  //   used   ← PAYG: 50−mins_left/60 or total_mins; SMB: planMinutes−mins_left
  //   rate   ← payg.plan_amount ($/min metered rate)                  via /agent-payg-details
  //   period ← payg.period_start / period_end                         via /agent-payg-details
  // Loading gate so the cards render ONLY after real data — no static flash.
  const [plan, setPlan] = useState(null)
  const [usage, setUsage] = useState(null)
  const [invoices, setInvoices] = useState([]) // Billing History / Milestones — real Stripe invoices
  const [planLoading, setPlanLoading] = useState(true)

  // "Try Again" (retry a failed invoice) — same in-app payInvoice + 3DS flow as
  // PaymentDue.jsx on Edit Profile. `payingId` is the invoice currently being
  // charged; payMessage surfaces the result. Bumping `refresh` re-runs the load
  // effect so a now-paid invoice flips to "paid" without a manual reload.
  const [payingId, setPayingId] = useState(null)
  const [payMessage, setPayMessage] = useState('')
  const [payMessageId, setPayMessageId] = useState(null) // which invoice the message belongs to
  const [refresh, setRefresh] = useState(0)

  // Reactivation — when a plan is FULLY ended (refunded / canceled, mode
  // 'checkout'), the Usage tab swaps the live usage cards for a reactivate
  // panel. This mirrors MySubscription.js on Edit Profile EXACTLY (same
  // getReactivationStatus + reactivateSubscription APIs, same checkout sheet
  // and PopUp), so the behaviour is identical wherever the user reactivates.
  //   reactivation.canReactivate — true if the ended plan can be restored
  //   reactivation.mode — 'checkout' (fully ended) vs 'resume' (active to
  //                        month end, which is handled as-is, no takeover)
  const [reactivation, setReactivation] = useState(null)
  const [reactivationLoaded, setReactivationLoaded] = useState(false) // status resolved?
  const [reactivateLoading, setReactivateLoading] = useState(false)
  const [reactPopup, setReactPopup] = useState(null) // { type, message }

  // Stripe billing_reason → the human "Reason" shown on invoice cards.
  const reasonLabel = (r) => {
    switch (r) {
      case 'subscription_threshold': return '100+ minutes used'
      case 'subscription_cycle': return 'Month end'
      case 'subscription_create': return 'Plan started'
      default: return 'Usage charge'
    }
  }

  useEffect(() => {
    if (!agentId) {
      setPlanLoading(false)
      return
    }
    let alive = true

    // try/catch/finally + allSettled — matches EditProfile's fetch style. One
    // failed call logs an error and falls back to null WITHOUT blanking the
    // others, so partial data still renders. UI is unchanged.
    const load = async () => {
      setPlanLoading(true)
      setReactivationLoaded(false) // re-gate the usage cards until status resolves
      try {
        // 1) Agent first — gives agentPlan, mins_left, planMinutes, userId.
        const agentRes = await getAgentById(agentId).catch((e) => {
          console.error('[UsageHistory] getAgentById failed:', e?.message)
          return null
        })
        if (!alive) return
        const userId = agentRes?.data?.userId || null

        // Reactivation status — independent, patches in when ready. Only a
        // fully-ended plan (mode 'checkout') triggers the Usage-tab takeover;
        // 'resume' (cancelled-but-active-to-month-end) is left to render as-is.
        getReactivationStatus(agentId)
          .then((s) => { if (alive) setReactivation(s || null) })
          .catch((e) => {
            console.error('[UsageHistory] getReactivationStatus failed:', e?.message)
            if (alive) setReactivation(null)
          })
          .finally(() => { if (alive) setReactivationLoaded(true) })

        // ── Progressive loading ──────────────────────────────────────────────
        // Each piece renders AS SOON AS its own data arrives — the page never
        // waits for the slowest call. Three independent groups:
        //   A) invoices          (getBillingInvoices)      → Billing History
        //   B) card / auto-pay   (getPaymentMethods)       → Auto Pay row
        //   C) upcoming invoice  (getUpcomingInvoice)      → Billing Milestones
        // The CORE usage/plan (agent + payg + sub) is computed together because
        // its fields are interdependent (amount = billable × rate) — splitting
        // those would risk half-computed values. But it does NOT wait for the
        // slow Stripe calls (B, C); those patch into `usage` when they resolve.

        // (A) Invoices — independent, show immediately when they arrive.
        getBillingInvoices()
          .then((invoiceRes) => {
            if (!alive) return
            const mapped = (invoiceRes?.invoices || []).map((inv) => {
              const dt = inv.created ? new Date(inv.created) : null
              const paid = inv.status === 'paid' || inv.amountPaid > 0
              const amt = paid ? inv.amountPaid : inv.amountDue
              return {
                id: inv.number || inv.id,
                stripeInvoiceId: inv.id,
                status: paid ? 'paid' : 'unpaid',
                // Raw ms timestamp — used to find the MOST RECENT bill so the
                // "100+ minutes used" note only shows while the threshold bill
                // is the latest one (it clears once a newer bill arrives).
                createdMs: dt && !isNaN(dt) ? dt.getTime() : null,
                date: dt && !isNaN(dt) ? fmtFull(dt) : '',
                time: dt && !isNaN(dt) ? dt.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }) : '',
                reason: reasonLabel(inv.billingReason),
                minutes: inv.minutesUsed != null ? `${inv.minutesUsed} min` : '',
                // "Minutes Used" only when real usage was billed (threshold /
                // month-end). A plan-purchase invoice (subscription_create) shows
                // the plan allowance, so it's just "Minutes", not "used".
                minutesLabel:
                  inv.billingReason === 'subscription_create' ? 'Minutes' : 'Minutes Used',
                amount: `$${Number(amt || 0).toFixed(2)}`,
                note: !paid ? "Payment couldn't be completed due to insufficient balance. Please maintain sufficient funds to avoid service interruption." : undefined,
                hostedInvoiceUrl: inv.hostedInvoiceUrl,
                invoicePdf: inv.invoicePdf,
              }
            })
            setInvoices(mapped)
          })
          .catch((e) => console.error('[UsageHistory] getBillingInvoices failed:', e?.message))

        // (B) Card / Auto Pay — independent, patches into usage when it arrives.
        getPaymentMethods()
          .then((cards) => {
            if (!alive) return
            const defaultCard = (cards?.paymentMethods || []).find((pm) => pm.isDefault)
            setUsage((prev) => ({
              ...(prev || {}),
              card: defaultCard ? `${defaultCard.brand} **** ${defaultCard.last4}` : '',
              autoPay: !!defaultCard,
              autoPayReady: true, // real card data has arrived (even if none → OFF is now correct)
            }))
          })
          .catch((e) => console.error('[UsageHistory] getPaymentMethods failed:', e?.message))

        // (C) Upcoming invoice (slowest — Stripe preview) — patches in when ready.
        getUpcomingInvoice(agentRes?.data?.subscriptionId)
          .then((upcomingRes) => {
            if (!alive) return
            setUsage((prev) => ({
              ...(prev || {}),
              upcomingAmount: upcomingRes?.hasUpcoming ? Number(upcomingRes.amountDue) : null,
              upcomingDate: upcomingRes?.hasUpcoming ? fmtDay(upcomingRes.periodEnd) : '',
            }))
          })
          .catch((e) => console.error('[UsageHistory] getUpcomingInvoice failed:', e?.message))

        // CORE — agent-payg-details + SMB subscription. These two feed the
        // interdependent plan/usage fields, so compute them together.
        const results = await Promise.allSettled([
          axios.post(`${API_BASE_URL}/agent-payg-details`, { agent_id: agentId }).then((r) => r.data?.data || null),
          userId ? getEndUserSubscriptions_Billings(userId) : Promise.resolve(null),
        ])
        const labels = ['agent-payg-details', 'endUserSubscriptions']
        const [payg, subsRes] = results.map((r, i) => {
          if (r.status === 'fulfilled') return r.value
          console.error(`[UsageHistory] ${labels[i]} failed:`, r.reason?.message || r.reason)
          return null
        })
        if (!alive) return

        const agentPlan = agentRes?.data?.agentPlan || null // AGENT table
        const planLower = String(agentPlan || '').trim().toLowerCase()
        // Known SMB plan names — if agentPlan is one of these it is DEFINITELY
        // SMB and must never be reclassified (its gauge uses the plan allowance).
        const SMB_PLANS = ['starter', 'scaler', 'growth']
        const isDefinitelySmb = SMB_PLANS.includes(planLower)
        // PAYG when the plan name says so OR — only when the name is blank/unknown
        // (NOT a real SMB plan) — a payg_subscriptions row exists. This makes PAYG
        // detection robust to agentPlan momentarily reading empty, WITHOUT ever
        // dragging a real SMB agent into the PAYG branch.
        const isPayg =
          ['payg', 'pay-as-you-go'].includes(planLower) ||
          (!isDefinitelySmb && !!payg)

        // SMB subscription (Starter/Scaler/Growth) = this agent's latestSubscription
        // from getEndUserSubscriptions_Billings (matched by subscriptionId → correct row).
        const myAgent = (subsRes?.agents || []).find((a) => a.agent_id === agentId)
        const sub = myAgent?.latestSubscription || null

        // Monthly fee source differs by plan type:
        //   PAYG → $5 flat "Number Assignment - Monthly Fee" line item (agent-payg-details).
        //   SMB  → subscriptionsss.plan_unit_amount (the real plan price, e.g. $299).
        let feeStr = null
        let feeNum = 0 // raw monthly fee number (used by UsageCard for SMB "$X / month")
        if (isPayg) {
          const monthlyFee = payg?.monthly_fee
          feeNum = Number(monthlyFee) || 0
          feeStr = monthlyFee != null ? `$${Number(monthlyFee).toFixed(2)}` : null
        } else if (sub?.plan_unit_amount != null) {
          feeNum = Number(sub.plan_unit_amount) || 0
          feeStr = `$${Number(sub.plan_unit_amount).toFixed(2)}`
        }
        setPlan({
          name: agentPlan, // e.g. "Pay-As-You-Go" / "Scaler"
          fee: feeStr,
          isYearly: String(sub?.interval || '').toLowerCase() === 'year',
        })

        // Per-minute metered rate — the "PAYG Extra" price. subscriptionsss.plan_unit_amount
        // is written from the METERED line item explicitly (robust), so prefer it;
        // fall back to payg_subscriptions.plan_amount.
        const rate = sub?.plan_unit_amount != null
          ? Number(sub.plan_unit_amount)
          : (payg?.plan_amount != null ? Number(payg.plan_amount) : 0)

        // ── PAYG free-50 tracking ─────────────────────────────────────────────
        // mins_left (seconds) is the FREE credit countdown; starts at 3000 (50 min)
        // and counts down. freeUsedUp = free exhausted → Case B (billing started).
        const FREE_MIN = 50
        const minsLeft = Number(agentRes?.data?.mins_left) || 0
        const freeUsedUp = minsLeft <= 0
        // Both derived from ONE floor so they always sum to 50 (no mismatch).
        const remainingFreeMin = Math.max(0, Math.min(FREE_MIN, Math.floor(minsLeft / 60)))
        const usedFreeMin = Math.max(0, FREE_MIN - remainingFreeMin)

        // total_mins = the metered minutes billed AFTER the free 50 (Case B). PAYG-only.
        const totalMins = Number(payg?.total_mins) || 0

        // Metered usage crosses 100 min → threshold bills & total_mins resets to
        // 0. So in Case B the gauge fills 0→100 (that's the paid-cycle cap).
        const PAYG_THRESHOLD_MIN = 100

        // ── Which "minutes used" + summary rows depend on the CASE ───────────
        // CASE A (mins_left > 0): still on free 50 → gauge "used of 50",
        //   Usage Summary shows Minutes Remaining (no billable/amount).
        // CASE B (mins_left = 0): free used up → gauge shows total_mins OF 100
        //   (the threshold cap; resets to 0 at 100), plus Billable + Amount.
        let usedMinutes, gaugeTotal, billable, remaining
        if (isPayg) {
          if (!freeUsedUp) {
            // Case A
            usedMinutes = usedFreeMin      // used of 50
            gaugeTotal = FREE_MIN          // gauge "of 50"
            remaining = remainingFreeMin   // Minutes Remaining
            billable = 0                   // not shown in Case A
          } else {
            // Case B
            usedMinutes = totalMins            // total_mins
            gaugeTotal = PAYG_THRESHOLD_MIN    // gauge "of 100" (threshold cap)
            remaining = 0
            billable = totalMins               // Billable = total_mins
          }
          // Safety net: PAYG must NEVER show "of 0". Right after a threshold
          // bill resets total_mins to 0, or if mins_left briefly reads 0 before
          // the payg row loads, the denominator could otherwise collapse. Any
          // PAYG cycle where the free 50 is used up caps at the 100-min
          // threshold, so pin the gauge total to 100.
          if (!gaugeTotal || gaugeTotal <= 0) gaugeTotal = PAYG_THRESHOLD_MIN
        } else {
          // SMB — fixed allowance. planMinutes (seconds) = total assigned minutes
          // (set from plan_mins on activation); mins_left = remaining.
          //   used  = assigned − remaining     gauge = "used of assigned"
          // Never use call-log minutes — the plan allowance is authoritative.
          const planSec = Number(agentRes?.data?.planMinutes) || 0
          const assignedMin = Math.max(0, Math.floor(planSec / 60))
          remaining = Math.max(0, Math.floor(minsLeft / 60))
          usedMinutes = Math.max(0, assignedMin - remaining)
          gaugeTotal = assignedMin // gauge "of <allowance>" — the SMB plan's own
          billable = 0             // allowance, ALWAYS. Never pinned to 100 here.
        }

        // Billing period comes from a DIFFERENT table per plan:
        //   PAYG → payg_subscriptions.period_start / period_end   (agent-payg-details)
        //   SMB  → subscriptionsss.current_period_start / _end     (subscription)
        const periodStartRaw = isPayg ? payg?.period_start : sub?.current_period_start
        const periodEndRaw = isPayg ? payg?.period_end : sub?.current_period_end
        // "Active until" for the Cancel line:
        //   PAYG → access_until (Stripe cancel_at / period_end)
        //   SMB  → subscription period end (or next renewal date)
        const activeUntilRaw = isPayg
          ? payg?.access_until
          : sub?.current_period_end || sub?.next_renewal_date

        // Merge into any card/auto-pay/upcoming that group (B)/(C) may have set
        // ALREADY (they can resolve before this core does). Functional update so
        // we never clobber those slow-arriving fields.
        // Does the agent's CURRENT subscription read as active? The agent's
        // subscriptionId is repointed to the new sub on reactivation, so this is
        // the truth about "do they have a live plan right now" — unlike the
        // reactivation API, which can resolve a stale cancelled sub and wrongly
        // say "ended". For PAYG, the payg row status; for SMB, the sub status.
        const subStatusLower = String(sub?.status || '').trim().toLowerCase()
        const paygStatusLower = String(payg?.status || '').trim().toLowerCase()
        const hasActiveSub = isPayg
          ? paygStatusLower === 'active'
          : ['active', 'trialing'].includes(subStatusLower)

        setUsage((prev) => ({
          ...(prev || {}),
          isPayg,                                              // drives which rows show
          hasActiveSub,                                        // current sub live? → overrides stale "ended"
          freeUsedUp,                                          // Case A (false) vs Case B (true)
          used: usedMinutes,                                   // gauge "Minutes Used" (case-aware)
          gaugeTotal,                                          // gauge "of X" (50 in Case A, null otherwise)
          remaining,                                           // Minutes Remaining (Case A / SMB)
          rate,                                                // $/min metered rate — PAYG
          monthlyFee: feeNum,                                  // $/month plan fee — SMB
          planName: agentPlan || '',                           // plan label — SMB subtitle
          billable,                                            // Billable Minutes — PAYG Case B
          amount: billable * rate,                             // Amount (so far) — PAYG Case B
          periodStart: fmtDay(periodStartRaw),
          periodEnd: fmtDay(periodEndRaw),
          activeUntil: fmtFull(activeUntilRaw), // with year, e.g. "Oct 15, 2026"
          // card / autoPay / autoPayReady / upcomingAmount / upcomingDate come
          // from groups (B) and (C) — do NOT set them here (would overwrite with
          // stale/empty values). `prev` already carries them if they've arrived.
        }))
      } catch (err) {
        console.error('[UsageHistory] load failed:', err?.message || err)
      } finally {
        if (alive) setPlanLoading(false)
      }
    }

    load()
    return () => {
      alive = false
    }
  }, [agentId, refresh])

  // Reactivate the ended plan. Ported 1:1 from MySubscription.handleReactivate-
  // Confirmed so the behaviour is IDENTICAL to Edit Profile: same
  // reactivateSubscription API, same embedded-checkout sheet for mode
  // 'checkout', same success/needs-card PopUp. No divergent logic here.
  const handleReactivate = async () => {
    if (!agentId || !reactivation || reactivateLoading) return
    // Resume needs a card on file — but on this page that's the Billing Info
    // tab. Point the user there rather than failing silently.
    if (reactivation.mode === 'resume' && reactivation.needsCard) {
      setReactPopup({ type: 'failed', message: 'Please add a card in Billing Info, then reactivate again.' })
      return
    }
    setReactivateLoading(true)
    try {
      const result = await reactivateSubscription(agentId, { embedded: isEmbeddedCheckoutEnabled() })
      if (result?.mode === 'checkout' && result?.clientSecret) {
        useCheckoutStore.getState().openCheckout({
          clientSecret: result.clientSecret,
          sessionId: result.sessionId,
          hostedUrl: result.checkoutUrl || null,
          meta: {
            agentId,
            planName: reactivation.planName,
            next: '/fast-agent-detail',
            extraParams: { reactivated: '1', session_id: result.sessionId },
          },
        })
        return
      }
      if (result?.mode === 'checkout' && result?.checkoutUrl) {
        window.location.href = result.checkoutUrl
        return
      }
      // Resume path — plan is active again; refresh the page data.
      setReactPopup({ type: 'success', message: 'Your plan is active again. Your number stays the same.' })
      setReactivation(null)
      setRefresh((n) => n + 1)
    } catch (error) {
      setReactPopup({
        type: 'failed',
        message: error?.code === 'card_required'
          ? `${error.message} You can add one in Billing Info.`
          : error?.message || 'Could not reactivate your plan.',
      })
    } finally {
      setReactivateLoading(false)
    }
  }

  // "Try Again" on a failed/unpaid invoice — retry the SAME open Stripe invoice
  // in-app (never a fresh checkout, which would double-bill when Stripe's own
  // retry lands). Identical flow to PaymentDue.jsx: payInvoice → if the bank
  // wants 3DS, finish the PaymentIntent here → else surface the error, with the
  // hosted Stripe page as a last-resort fallback.
  const handleTryAgain = async (inv) => {
    const invoiceId = inv?.stripeInvoiceId
    if (!invoiceId || payingId) return
    setPayingId(invoiceId)
    setPayMessageId(invoiceId)
    setPayMessage('')
    try {
      const result = await payInvoice(invoiceId)

      if (result?.status === 'requires_action' && result?.clientSecret) {
        const stripe = await stripePromise
        if (!stripe) throw new Error("Payment couldn't be completed. Please contact support.")
        const { error: confirmError } = await stripe.confirmCardPayment(result.clientSecret)
        if (confirmError) {
          throw new Error(confirmError.message || "Your bank didn't approve the payment. Please try again.")
        }
      }

      setPayMessage('Payment received. Updating your invoices…')
      // The webhook marks the invoice paid; re-fetch so the row flips to "paid".
      setRefresh((p) => p + 1)
    } catch (err) {
      // Fall back to Stripe's hosted page when the server couldn't reach the
      // PaymentIntent (it always returns a hostedInvoiceUrl in that case).
      const hosted = err?.hostedInvoiceUrl || inv?.hostedInvoiceUrl
      if (hosted) {
        setPayMessage('Opening secure Stripe payment page…')
        window.open(hosted, '_blank')
      } else {
        setPayMessage(err?.message || 'Payment failed. Please try again.')
      }
    } finally {
      setPayingId(null)
    }
  }

  const freeMinutes = 50 // Welcome-offer free minutes (onboarding credit)

  // Billing Milestones = condensed timeline: an "Upcoming invoice" (from Stripe) +
  // the latest paid bill(s). Note line is PAYG-only.
  const milestones = []
  if (usage) {
    // Upcoming invoice — the REAL Stripe preview (usage + $5 + proration for PAYG,
    // or the flat renewal for SMB). PAYG accrues → "Ongoing"; SMB is fixed → "Renews".
    if (usage.upcomingAmount != null) {
      milestones.push({
        id: 'upcoming',
        title: 'Upcoming invoice',
        // Same wording for PAYG and SMB (per QA): "At Billing Cycle End" + the
        // cycle-end date, with an "Ongoing" status.
        sub: `At Billing Cycle End${usage.upcomingDate ? ` • ${usage.upcomingDate}` : ''}`,
        amount: `$${Number(usage.upcomingAmount).toFixed(2)}`,
        status: 'Ongoing',
        done: false,
      })
    }
    // Latest past invoice only (real Stripe bill). Full list is in Billing History.
    invoices.slice(0, 1).forEach((inv) => {
      milestones.push({
        id: inv.id,
        title: inv.status === 'paid' ? 'Bill Generated' : 'Bill Pending',
        sub: `${inv.minutes || ''}${inv.minutes && inv.date ? ' • ' : ''}${inv.date || ''}`.trim(),
        amount: inv.amount,
        status: inv.status === 'paid' ? 'Paid' : 'Unpaid',
        done: inv.status === 'paid',
      })
    })
  }
  // Note line — PAYG only, and ONLY while the threshold bill is the MOST RECENT
  // bill. Billing Milestones shows just the upcoming + the single latest bill,
  // so the note tracks that latest bill: it appears when a "100+ minutes used"
  // (threshold) bill is the newest, and clears the moment a newer bill (e.g. the
  // month-end cycle bill) is generated. A fresh threshold next cycle shows it
  // again with the new date.
  let milestoneNote = null
  if (usage?.isPayg) {
    // Newest generated bill by timestamp. (subscription_create/plan-start bills
    // don't carry the threshold reason, so they naturally clear the note too.)
    const latestInv = invoices
      .filter((i) => i.createdMs != null)
      .reduce((newest, i) => (!newest || i.createdMs > newest.createdMs ? i : newest), null)
    if (latestInv?.reason === '100+ minutes used' && latestInv?.date) {
      milestoneNote = `100+ minutes used on ${latestInv.date}. A bill was generated immediately. Additional usage will be billed on ${usage.periodEnd || 'month end'}.`
    }
  }

  // Each section fades/slides in with a staggered delay.
  const section = (child, index) => (
    <div className={styles.reveal} style={{ animationDelay: `${index * 90}ms` }}>
      {child}
    </div>
  )

  // Plan FULLY ended (refunded / canceled) → reactivation mode 'checkout'. This
  // is the ONLY case that takes over the Usage tab (removes the live usage cards
  // and shows a reactivate panel). 'resume' (cancelled but active to month end)
  // renders as-is. planLoading guards against a flash before the status resolves.
  //
  // GUARD: the agent may have taken a NEW plan after cancelling an old one (e.g.
  // cancel PAYG → buy Growth). The reactivation API can still resolve the OLD
  // canceled sub, which would wrongly flag "ended" over a live plan. So only
  // treat it as ended when the CURRENT plan is free/blank, OR the plan being
  // reactivated is the SAME as the current plan. If agentPlan is a different
  // real plan, the agent moved on — not ended.
  const curPlanKey = String(plan?.name || '').trim().toLowerCase()
  const reactPlanKey = String(reactivation?.planName || '').trim().toLowerCase()
  const curPlanIsFree = curPlanKey === '' || curPlanKey === 'free'
  const endedMatchesCurrent = curPlanIsFree || curPlanKey === reactPlanKey
  const isEnded =
    reactivationLoaded &&
    reactivation?.canReactivate &&
    reactivation?.mode === 'checkout' &&
    endedMatchesCurrent &&
    // If the agent's CURRENT subscription is active (e.g. they cancelled then
    // reactivated → a new active sub the reactivation API didn't resolve), it is
    // NOT ended. This is the fix for "cancel → reactivate same plan" showing the
    // reactivate screen over a live plan.
    !usage?.hasActiveSub
  // The plan name to reference in the reactivate copy — the last plan they had.
  const endedPlanName = reactivation?.planName || plan?.name || 'your'
  // A real, active plan is any plan name that isn't "free"/blank. When we already
  // know the plan is real, show the fee IMMEDIATELY — no need to wait for the
  // reactivation status (which only matters for a "free" plan, to tell ended
  // from never-subscribed). This avoids the fee/See-Plan looking "ended" for a
  // moment during a normal active-plan load or a reactivation reload.
  // Real active plan = a non-free plan name AND not ended. The `!isEnded` guard
  // matters for PAYG: cancel leaves agentPlan as "PAYG Extra" in the DB (only
  // Stripe knows it's canceled), so without it a cancelled PAYG would look
  // "real active" and wrongly show the empty Monthly Plan Fee heading. (Reuses
  // curPlanKey computed above.)
  const planIsRealActive = !planLoading && !isEnded && curPlanKey !== '' && curPlanKey !== 'free'

  // Per-plan icon (same mapping as Manage Subscription / RecommendedPlan) so the
  // Current Plan icon matches the plan — Growth/Scaler have their own, PAYG uses
  // the payg icon. Empty while loading (not ready) so no default icon flashes and
  // then changes. When ended/free → the neutral payg icon.
  const PLAN_ICONS = {
    scaler: '/svg/scaler-icon_new.svg',
    growth: '/svg/growth-icon_icon.svg',
    starter: '/svg/starter-icon_new.svg',
  }
  // Per-plan brand colour for the subtitle — SAME as Manage Subscription's plan
  // name colours (Scaler orange, Growth blue, Starter green). PAYG/ended/free
  // stay the default green (empty → PlanCard uses its green default).
  const PLAN_COLORS = {
    scaler: '#FE5E00',
    growth: '#00A4FF',
    starter: '#E83BAC',
  }
  // Light circle-background behind the plan icon — SAME tints as the upgrade
  // cards (Scaler light-orange, Growth light-blue, Starter light-pink). PAYG /
  // ended / free keep the green default (empty → PlanCard's default green).
  const PLAN_ICON_BG = {
    scaler: '#fff8f4',
    growth: '#e7f7ff',
    starter: '#ffeef8',
  }
  const planReady = !planLoading && reactivationLoaded
  const currentPlanIcon = !planReady
    ? '' // loading → show nothing (no default flash)
    : (isEnded || curPlanIsFree || usage?.isPayg)
      ? '/svg/payg_icon.svg'
      : (PLAN_ICONS[curPlanKey] || '/svg/payg_icon.svg')

  return (
    <div className={styles.page}>
      <div className={styles.stickyHeader}>
        <HeaderBar title="Usage & Billing" />

        <div className={styles.tabs}>
          <div className={styles.tabsInner}>
          <button
            type="button"
            className={`${styles.tab} ${activeTab === 'usage' ? styles.tabActive : ''}`}
            onClick={() => setActiveTab('usage')}
          >
            Usage
          </button>
          <button
            type="button"
            className={`${styles.tab} ${activeTab === 'invoices' ? styles.tabActive : ''}`}
            onClick={() => setActiveTab('invoices')}
          >
            Invoices
          </button>
          <button
            type="button"
            className={`${styles.tab} ${activeTab === 'billingInfo' ? styles.tabActive : ''}`}
            onClick={() => setActiveTab('billingInfo')}
          >
            Billing Info
          </button>
            {/* Sliding active indicator */}
            <span
              className={`${styles.indicator} ${activeTab === 'invoices' ? styles.indicatorInvoices : ''} ${activeTab === 'billingInfo' ? styles.indicatorBillingInfo : ''}`}
            />
          </div>
        </div>
      </div>

      {activeTab === 'usage' ? (
        <div className={styles.content}>
          {reactivationLoaded && !isEnded && usage?.isPayg && usage?.freeUsedUp
            ? section(
                <OfferBanner
                  text={`Your first month's ${freeMinutes} free minutes have been used. Calls are now billed at $${Number(usage?.rate ?? 0).toFixed(2)}/min on your PAYG plan`}
                />,
                0,
              )
            : null}
          {(planLoading || !reactivationLoaded)
            ? section(
                <PlanCard
                  // Loading → grey shimmer skeleton for the plan name/fee, so the
                  // card doesn't show an empty "Current Plan" while data loads.
                  loading
                  onSeePlan={() => navigate('/manage-subscription', { state: { agentId } })}
                />,
                1,
              )
            : section(
                <PlanCard
                  // Ended/refunded → "free". PAYG → the friendly "PAYG - pay as
                  // you go" label (same as Manage Subscription) instead of the
                  // raw DB name "PAYG Extra", so the two pages match. SMB → its
                  // real plan name.
                  planTagline={
                    isEnded
                      ? 'free'
                      : (usage?.isPayg ? 'PAYG - pay as you go' : (plan?.name || ''))
                  }
                  // Per-plan icon (Growth/Scaler have their own; PAYG/free use
                  // the payg icon) — matches Manage Subscription.
                  icon={currentPlanIcon}
                  // Per-plan subtitle colour (Scaler orange / Growth blue) for an
                  // SMB plan — same as Manage Subscription's plan name colour.
                  // PAYG / ended / free keep the default green (empty prop).
                  taglineColor={
                    isEnded || usage?.isPayg ? '' : (PLAN_COLORS[curPlanKey] || '')
                  }
                  // Per-plan light circle background behind the icon (matches
                  // Manage Subscription). PAYG / ended → green default (empty).
                  iconBg={
                    isEnded || usage?.isPayg ? '' : (PLAN_ICON_BG[curPlanKey] || '')
                  }
                  // Plan fully ended/refunded → hide the whole Monthly Plan Fee
                  // block (label + value); there's no active plan to charge. Also
                  // wait for the reactivation status to resolve, so the fee never
                  // flashes before the ended-state takeover. See Plan link stays
                  // (PlanCard moves it up to the fee's spot when the fee is gone).
                  // Show the fee the moment we know it's a real active plan.
                  // For a "free" plan, wait for reactivation status (then it's
                  // either ended → hide, or stays hidden) so the fee never
                  // flashes on an ended plan.
                  showFee={planIsRealActive || (reactivationLoaded && !isEnded)}
                  // Only the ENDED state moves See Plan to the top-right. During
                  // loading / reactivation-pending it stays off so the card
                  // doesn't look "ended" while a real plan is coming.
                  seePlanInTop={isEnded}
                  fee={isEnded ? '' : (plan?.fee || '')}
                  // "Usage charges are billed separately" is PAYG-only (usage billed
                  // on top of the $5). SMB plans include their minutes → hide it.
                  // Also hidden when ended (no active usage billing).
                  note={!isEnded && usage?.isPayg ? 'Usage charges are billed separately.' : ''}
                  onSeePlan={() => navigate('/manage-subscription', { state: { agentId } })}
                />,
                1,
              )}
          {/* Plan ended → reactivate panel replaces Current Usage + Usage
              Summary. Billing Milestones below stays (their past bills). */}
          {isEnded
            ? section(
                <div className={styles.reactivatePanel}>
                  <p className={styles.reactivateInfo}>
                    Your <strong>{endedPlanName}</strong> plan has ended. Reactivate to
                    restore your AI receptionist — your previous plan picks up right
                    where it left off.
                  </p>
                  <button
                    type="button"
                    className={styles.reactivateBtn}
                    onClick={handleReactivate}
                    disabled={reactivateLoading}
                  >
                    {reactivateLoading ? 'Reactivating…' : 'Reactivate'}
                  </button>
                </div>,
                2,
              )
            : null}
          {/* Loading → grey shimmer skeleton for Current Usage + Usage Summary so
              the user never sees a blank page while the slow calls finish. ONE
              condition (loaded or not), same pattern as Manage Subscription.
              Replaced by the real gauge (active) or the Reactivate panel (ended)
              once loaded — never lingers for an ended plan. No data/logic touched. */}
          {!reactivationLoaded && (
            <>
              {/* Current Usage card skeleton — gauge + billed-at + note bar. */}
              {section(
                <div className={styles.usageSkeleton} aria-hidden="true">
                  <span className={`${styles.usk} ${styles.uskTitle}`} />
                  <div className={styles.uskRow}>
                    <span className={`${styles.usk} ${styles.uskGauge}`} />
                    <div className={styles.uskCol}>
                      <span className={`${styles.usk} ${styles.uskSm}`} />
                      <span className={`${styles.usk} ${styles.uskLg}`} />
                      <span className={`${styles.usk} ${styles.uskMd}`} />
                    </div>
                  </div>
                  <span className={`${styles.usk} ${styles.uskBar}`} />
                </div>,
                2,
              )}
              {/* Usage Summary skeleton — a few label/value rows. */}
              {section(
                <div className={styles.summarySkeleton} aria-hidden="true">
                  <span className={`${styles.usk} ${styles.uskTitle}`} />
                  <div className={styles.uskLine}>
                    <span className={`${styles.usk} ${styles.uskMd}`} />
                    <span className={`${styles.usk} ${styles.uskSm}`} />
                  </div>
                  <div className={styles.uskLine}>
                    <span className={`${styles.usk} ${styles.uskMd}`} />
                    <span className={`${styles.usk} ${styles.uskSm}`} />
                  </div>
                  <div className={styles.uskLine}>
                    <span className={`${styles.usk} ${styles.uskMd}`} />
                    <span className={`${styles.usk} ${styles.uskSm}`} />
                  </div>
                </div>,
                3,
              )}
            </>
          )}
          {reactivationLoaded && !isEnded && section(
            <UsageCard
              isPayg={usage ? usage.isPayg : true}
              freeUsedUp={!!usage?.freeUsedUp}
              used={usage?.used ?? 0}
              total={usage?.gaugeTotal ?? null}
              rate={usage?.rate ?? 0}
              monthlyFee={usage?.monthlyFee ?? 0}
              planName={usage?.planName ?? ''}
              periodStart={usage?.periodStart ?? ''}
              periodEnd={usage?.periodEnd ?? ''}
              freeMinutes={freeMinutes}
            />,
            2,
          )}
          {reactivationLoaded && !isEnded && section(
            <UsageSummary
              autoPay={!!usage?.autoPay}
              autoPayReady={!!usage?.autoPayReady}
              card={usage?.card || ''}
              onAutoPay={() => setActiveTab('billingInfo')}
              rows={
                !usage
                  ? []
                  : !usage.isPayg
                    ? [
                        // SMB: prepaid allowance → used + remaining (no billable/amount).
                        { label: 'Total Minutes Used', value: `${usage.used ?? 0} min` },
                        { label: 'Minutes Remaining', value: `${usage.remaining ?? 0} min` },
                      ]
                    : !usage.freeUsedUp
                      ? [
                          // PAYG Case A — still on free 50: used + remaining (no billable/amount).
                          { label: 'Total Minutes Used', value: `${usage.used ?? 0} min` },
                          { label: 'Minutes Remaining', value: `${usage.remaining ?? 0} min` },
                        ]
                      : [
                          // PAYG Case B — free 50 used up: billable + amount.
                          { label: 'Total Minutes Used', value: `${usage.used ?? 0} min` },
                          { label: 'Billable Minutes', value: `${usage.billable ?? 0} min` },
                          { label: 'Amount (so far)', value: `$${Number(usage.amount ?? 0).toFixed(2)}` },
                        ]
              }
            />,
            3,
          )}
          {milestones.length > 0 && section(
            <BillingMilestones
              milestones={milestones}
              note={milestoneNote}
              onSeeAll={() => setActiveTab('invoices')}
            />,
            4,
          )}
          {/* Current Charges = PAYG + free 50 used up (Case B) only. Hidden while
              on free minutes (Case A), for SMB (flat fee), and when ended. Also
              waits for reactivation status to avoid a flash before the takeover. */}
          {reactivationLoaded && !isEnded && usage?.isPayg && usage?.freeUsedUp && section(
            <CurrentCharges
              amount={Number(usage?.amount ?? 0)}
              minutes={usage?.billable ?? 0}
              rate={usage?.rate ?? 0}
            />,
            5,
          )}
        </div>
      ) : activeTab === 'invoices' ? (
        <div className={styles.content}>
          {/* PAYG-only: the "100+ min or month-end" rule is specific to PAYG.
              SMB plans (Starter/Scaler/Growth) bill on a fixed monthly cycle, so
              this banner would be misleading for them. */}
          {usage?.isPayg && !isEnded && section(<BillingWorksBanner />, 0)}
          {section(
            <BillingHistory
              invoices={invoices}
              onDownload={(inv) => {
                const url = inv.invoicePdf || inv.hostedInvoiceUrl
                if (url) window.open(url, '_blank')
              }}
              onInvoiceAction={(inv) => {
                // Unpaid → "Try Again": retry in-app (payInvoice + 3DS).
                // Paid → "View Details": open the hosted Stripe invoice.
                if (inv.status !== 'paid') {
                  handleTryAgain(inv)
                } else {
                  const url = inv.hostedInvoiceUrl || inv.invoicePdf
                  if (url) window.open(url, '_blank')
                }
              }}
              payingId={payingId}
              payMessage={payMessage}
              payMessageId={payMessageId}
              onUpgrade={() => navigate('/manage-subscription', { state: { agentId } })}
            />,
            1,
          )}
          {section(
            <div className={styles.manageFooter}>
              <button
                type="button"
                className={styles.manageLink}
                onClick={() => navigate('/manage-subscription', { state: { agentId } })}
              >
                Manage Subscription
              </button>
              {/* "Active until" only when there IS an active plan. A fully
                  ended/refunded plan has no remaining-active date, so hide it
                  (the Manage Subscription link above stays — used to reactivate). */}
              {!isEnded && (
                <p className={styles.cancelNote}>
                  Your Plan will remain active until {usage?.activeUntil || '—'}
                </p>
              )}
            </div>,
            2,
          )}
        </div>
      ) : (
        <div className={`${styles.content} ${styles.billingInfoContent}`}>
          {section(<BillingInfo />, 0)}
        </div>
      )}

      {/* Reactivation result — same PopUp component/flow as Edit Profile. */}
      {reactPopup && (
        <PopUp
          type={reactPopup.type}
          message={reactPopup.message}
          onClose={() => setReactPopup(null)}
        />
      )}
    </div>
  )
}

export default UsageHistory
